import {
  currencySchema,
  DEFINABLE_VAULT_KINDS,
  INSTITUTION_TRANSFER_STATUSES,
  INSTITUTION_VAULT_KINDS,
} from '@btsis/core';
import { oc } from '@orpc/contract';
import { z } from 'zod';
import { commonErrors } from './common/errors.js';
import { moneySchema } from './common/money.js';
import { paginated, paginationInputSchema } from './common/pagination.js';

const guarded = {
  UNAUTHENTICATED: commonErrors.UNAUTHENTICATED,
  PERMISSION_DENIED: commonErrors.PERMISSION_DENIED,
  TWO_FACTOR_REQUIRED: commonErrors.TWO_FACTOR_REQUIRED,
  OUT_OF_SCOPE: commonErrors.OUT_OF_SCOPE,
  NOT_FOUND: commonErrors.NOT_FOUND,
  CONFLICT: commonErrors.CONFLICT,
  VALIDATION_FAILED: commonErrors.VALIDATION_FAILED,
};

const reasonSchema = z.string().trim().min(1).max(500);

const positiveMinorSchema = z
  .string()
  .regex(/^\d{1,18}$/, 'must be an integer in minor units')
  .refine((value) => /[1-9]/.test(value), 'must be above zero');

const vaultNameSchema = z.string().trim().min(1).max(120);

export const institutionVaultSchema = z.object({
  id: z.uuid(),
  kind: z.enum(INSTITUTION_VAULT_KINDS),
  name: z.string().nullable(),
  isActive: z.boolean(),
  balances: z.array(moneySchema),
});

export type InstitutionVault = z.infer<typeof institutionVaultSchema>;

/**
 * One organization's reconciled money at one institution, on one of its
 * accounts, in one currency — docs/decisions/0129 §3. `collected` is what the
 * confirmed matches credited the institution's cari with (0121), net of
 * reversals; `transferred` what completed transfers moved out; `committed` what
 * requested and approved ones hold; `available` what may still be asked for.
 *
 * **The account is the organization's own detail.** Which organizations' money
 * is held at an institution, and how much, is what an institution's reader
 * already reads off its cari (0121), so a row of an organization the reader's
 * scope does not wholly hold (`organizationInScope: false`) is still shown —
 * but with the account null, and the organization's accounts folded into one
 * row per currency. Only a reader of the organization sees an account.
 */
export const heldMoneySchema = z.object({
  organizationId: z.uuid(),
  organizationCode: z.string(),
  organizationName: z.string(),
  accountId: z.uuid().nullable(),
  accountLabel: z.string().nullable(),
  currency: currencySchema,
  collected: moneySchema,
  transferred: moneySchema,
  committed: moneySchema,
  available: moneySchema,
  /**
   * What one transfer request from this account may ask for: `available` and the
   * account's balance, whichever binds, by the function the request write
   * refuses by (`transferCapacity`). Null where the account is not shown.
   */
  transferable: moneySchema.nullable(),
  /** Which limit `transferable` meets: the reconciled room, or the account's balance. */
  limitedBy: z.enum(['room', 'account']).nullable(),
  organizationInScope: z.boolean(),
});

export type HeldMoney = z.infer<typeof heldMoneySchema>;

export const institutionVaultViewSchema = z.object({
  institution: z.object({
    id: z.uuid(),
    code: z.string(),
    name: z.string(),
    countryName: z.string(),
    isActive: z.boolean(),
  }),
  vaults: z.array(institutionVaultSchema),
  held: z.array(heldMoneySchema),
  mayManageVaults: z.boolean(),
});

export type InstitutionVaultView = z.infer<typeof institutionVaultViewSchema>;

const refSchema = z.object({ id: z.uuid(), code: z.string(), name: z.string() });

export const institutionTransferSchema = z.object({
  id: z.uuid(),
  fromOrganization: refSchema,
  /** Null when the reader's scope does not wholly hold the organization (`organizationInScope`). */
  account: z.object({ id: z.uuid(), label: z.string() }).nullable(),
  institution: refSchema,
  vault: z.object({
    id: z.uuid(),
    kind: z.enum(INSTITUTION_VAULT_KINDS),
    name: z.string().nullable(),
  }),
  amount: moneySchema,
  reason: z.string(),
  status: z.enum(INSTITUTION_TRANSFER_STATUSES),
  requestedByUserId: z.uuid(),
  requestedByName: z.string().nullable(),
  requestedAt: z.string(),
  approvedByName: z.string().nullable(),
  approvedAt: z.string().nullable(),
  completedByName: z.string().nullable(),
  completedAt: z.string().nullable(),
  cancelledByName: z.string().nullable(),
  cancelledAt: z.string().nullable(),
  cancellationReason: z.string().nullable(),
  reversedByName: z.string().nullable(),
  reversedAt: z.string().nullable(),
  reversalReason: z.string().nullable(),
  organizationInScope: z.boolean(),
  institutionInScope: z.boolean(),
});

export type InstitutionTransfer = z.infer<typeof institutionTransferSchema>;

export const institutionTransferListInputSchema = paginationInputSchema.extend({
  institutionId: z.uuid().optional(),
  organizationId: z.uuid().optional(),
  status: z.enum(INSTITUTION_TRANSFER_STATUSES).optional(),
});

export type InstitutionTransferListInput = z.infer<typeof institutionTransferListInputSchema>;

export const createInstitutionVaultInputSchema = z.object({
  institutionId: z.uuid(),
  kind: z.enum(DEFINABLE_VAULT_KINDS),
  name: vaultNameSchema,
});

export type CreateInstitutionVaultInput = z.infer<typeof createInstitutionVaultInputSchema>;

export const updateInstitutionVaultInputSchema = z
  .object({ id: z.uuid(), name: vaultNameSchema.optional(), isActive: z.boolean().optional() })
  .refine((input) => input.name !== undefined || input.isActive !== undefined, 'nothing to change');

export type UpdateInstitutionVaultInput = z.infer<typeof updateInstitutionVaultInputSchema>;

export const requestInstitutionTransferInputSchema = z.object({
  fromOrganizationId: z.uuid(),
  accountId: z.uuid(),
  institutionId: z.uuid(),
  vaultId: z.uuid(),
  amountMinor: positiveMinorSchema,
  currency: currencySchema,
  reason: reasonSchema,
});

export type RequestInstitutionTransferInput = z.infer<typeof requestInstitutionTransferInputSchema>;

const transferResultSchema = z.object({ transfer: institutionTransferSchema });

/**
 * An institution's vaults and the money organizations hold for it —
 * docs/decisions/0129. Reads are `finance.view`, in the institution's scope;
 * a vault is defined behind `orgUnit.edit`; a transfer is asked for
 * (`fundTransfer.create`) and approved (`fundTransfer.approve`) in the
 * organization's scope by two people, completed (`fundTransfer.complete`) in
 * the institution's, and undone by an approver holding both.
 */
export const institutionVaultContract = {
  get: oc
    .route({ method: 'GET', path: '/institution-vaults/{institutionId}' })
    .errors(guarded)
    .input(z.object({ institutionId: z.uuid() }))
    .output(institutionVaultViewSchema),

  create: oc
    .route({ method: 'POST', path: '/institution-vaults' })
    .errors(guarded)
    .input(createInstitutionVaultInputSchema)
    .output(z.object({ vault: institutionVaultSchema })),

  update: oc
    .route({ method: 'PATCH', path: '/institution-vaults/vault/{id}' })
    .errors(guarded)
    .input(updateInstitutionVaultInputSchema)
    .output(z.object({ vault: institutionVaultSchema })),

  transfers: oc
    .route({ method: 'GET', path: '/institution-transfers' })
    .errors(guarded)
    .input(institutionTransferListInputSchema)
    .output(paginated(institutionTransferSchema)),

  requestTransfer: oc
    .route({ method: 'POST', path: '/institution-transfers' })
    .errors(guarded)
    .input(requestInstitutionTransferInputSchema)
    .output(transferResultSchema),

  approveTransfer: oc
    .route({ method: 'POST', path: '/institution-transfers/{id}/approve' })
    .errors(guarded)
    .input(z.object({ id: z.uuid() }))
    .output(transferResultSchema),

  completeTransfer: oc
    .route({ method: 'POST', path: '/institution-transfers/{id}/complete' })
    .errors(guarded)
    .input(z.object({ id: z.uuid() }))
    .output(transferResultSchema),

  cancelTransfer: oc
    .route({ method: 'POST', path: '/institution-transfers/{id}/cancel' })
    .errors(guarded)
    .input(z.object({ id: z.uuid(), reason: reasonSchema }))
    .output(transferResultSchema),

  reverseTransfer: oc
    .route({ method: 'POST', path: '/institution-transfers/{id}/reverse' })
    .errors(guarded)
    .input(z.object({ id: z.uuid(), reason: reasonSchema }))
    .output(transferResultSchema),
};
